"use client";

import { c as _c } from "react/compiler-runtime";
import { HelpCircle } from "@untitledui/icons";
import { truncateAddress } from "@utils";
import Link from "next/link";
import type { ReactNode } from "react";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { LINKS } from "@/consts/global";
import { computeTurnover, formatTurnover } from "@/lib/pools/vol-tvl";
import { cx } from "@/utils/cx";
import type { Pool } from "../../data";
import { getCoinGeckoProfileUrl } from "../../utils/coingecko";
import { getNetworkBadgeColor } from "../../utils/networkBadgeColor";
import { formatProtocolBadgeLabel, getProtocolBadgeColor } from "../../utils/protocolBadge";
import { getUniswapPoolUrl } from "../../utils/uniswap";
import { FavoriteStar } from "../FavoriteStar";
import { PoolAddressHoverCard } from "../PoolAddressHoverCard";
import { AnalyzeButton } from "./AnalyzeButton";
import { visibleCardMetricIds } from "./column-visibility";
import { APR_1D_TOOLTIP, APR_7D_TOOLTIP, APR_30D_TOOLTIP, VOL_TVL_1D_TOOLTIP, VOL_TVL_7D_TOOLTIP, VOL_TVL_30D_TOOLTIP, formatApr, formatTokenSymbol } from "./PoolsTableShared";
interface PoolCardProps {
  pool: Pool;
  hasFavoritesAccess: boolean;
  isFavorited: boolean;
  isLoggedIn: boolean;
  hasAccessToPremiumFeatures: boolean;
  has7DayPerformance: boolean;
  has30DayPerformance: boolean;
  hiddenColumnIds: readonly string[];
  isUnfavoriting?: boolean;
  onAnalyze: (poolAddress: string, network: string) => void;
  onUnfavoriting?: (poolAddress: string) => void;
}
function MetricLabel(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="text-xs font-semibold text-text-quaternary">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function MetricTooltipLabel(t0) {
  const $ = _c(11);
  const {
    label,
    tooltip
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <MetricLabel>{label}</MetricLabel>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${label} info`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HelpCircle className="size-4 shrink-0" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = <TooltipTrigger aria-label={t2} className="cursor-pointer text-fg-quaternary hover:text-fg-quaternary_hover">{t3}</TooltipTrigger>;
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4 || $[6] !== tooltip) {
    t5 = <Tooltip title={tooltip} placement="top">{t4}</Tooltip>;
    $[5] = t4;
    $[6] = tooltip;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t5) {
    t6 = <div className="flex items-center gap-1">{t1}{t5}</div>;
    $[8] = t1;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function Metric(t0) {
  const $ = _c(6);
  const {
    label,
    tooltip,
    children
  } = t0;
  let t1;
  if ($[0] !== label || $[1] !== tooltip) {
    t1 = tooltip ? <MetricTooltipLabel label={label} tooltip={tooltip} /> : <MetricLabel>{label}</MetricLabel>;
    $[0] = label;
    $[1] = tooltip;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <div className="flex min-w-0 flex-col gap-2">{t1}{children}</div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function LockedAprViewLink() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href={LINKS.Plans.href} className="text-sm font-medium text-brand-secondary underline underline-offset-2 hover:text-brand-secondary_hover">View</Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const TOKEN_PROFILE_LINK_CLASS = "truncate underline underline-offset-2 hover:text-brand-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring";
function PoolCardTokenPairLabels(t0) {
  const $ = _c(18);
  const {
    pool
  } = t0;
  let t1;
  if ($[0] !== pool.token1) {
    t1 = formatTokenSymbol(pool.token1);
    $[0] = pool.token1;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label1 = t1;
  let t2;
  if ($[2] !== pool.token2) {
    t2 = formatTokenSymbol(pool.token2);
    $[2] = pool.token2;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const label2 = t2;
  let t3;
  if ($[4] !== pool.coinGeckoDisplayToken1Id) {
    t3 = getCoinGeckoProfileUrl(pool.coinGeckoDisplayToken1Id);
    $[4] = pool.coinGeckoDisplayToken1Id;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const url1 = t3;
  let t4;
  if ($[6] !== pool.coinGeckoDisplayToken2Id) {
    t4 = getCoinGeckoProfileUrl(pool.coinGeckoDisplayToken2Id);
    $[6] = pool.coinGeckoDisplayToken2Id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const url2 = t4;
  let t5;
  if ($[8] !== label1 || $[9] !== url1) {
    t5 = url1 ? <a href={url1} target="_blank" rel="noopener noreferrer" className={TOKEN_PROFILE_LINK_CLASS} title={label1}>{label1}</a> : <span className="truncate" title={label1}>{label1}</span>;
    $[8] = label1;
    $[9] = url1;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="shrink-0 text-text-secondary">/</span>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== label2 || $[13] !== url2) {
    t7 = url2 ? <a href={url2} target="_blank" rel="noopener noreferrer" className={TOKEN_PROFILE_LINK_CLASS} title={label2}>{label2}</a> : <span className="truncate" title={label2}>{label2}</span>;
    $[12] = label2;
    $[13] = url2;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div className="flex min-w-0 items-center gap-1 text-lg leading-7 font-medium text-text-primary">{t5}{t6}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
export function PoolCard(t0) {
  const $ = _c(69);
  const {
    pool,
    hasFavoritesAccess,
    isFavorited,
    hasAccessToPremiumFeatures,
    has7DayPerformance,
    has30DayPerformance,
    hiddenColumnIds,
    isUnfavoriting: t1,
    onAnalyze,
    onUnfavoriting
  } = t0;
  const isUnfavoriting = t1 === undefined ? false : t1;
  const t2 = isUnfavoriting && "opacity-0";
  let t3;
  if ($[0] !== t2) {
    t3 = cx("flex h-full min-w-0 flex-col gap-4 rounded-lg border border-border-primary bg-primary p-4 shadow-xs transition-opacity", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== isUnfavoriting) {
    t4 = isUnfavoriting ? {
      transitionDuration: "800ms"
    } : undefined;
    $[2] = isUnfavoriting;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== hasFavoritesAccess || $[5] !== isFavorited || $[6] !== onUnfavoriting || $[7] !== pool.network || $[8] !== pool.pool) {
    t5 = hasFavoritesAccess && <FavoriteStar poolAddress={pool.pool} network={pool.network} isFavorited={isFavorited} onUnfavoriting={onUnfavoriting} />;
    $[4] = hasFavoritesAccess;
    $[5] = isFavorited;
    $[6] = onUnfavoriting;
    $[7] = pool.network;
    $[8] = pool.pool;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = pool.pool;
  const t7 = pool.network;
  let t8;
  if ($[10] !== pool.network || $[11] !== pool.pool) {
    t8 = getUniswapPoolUrl(pool.network, pool.pool);
    $[10] = pool.network;
    $[11] = pool.pool;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = pool.pool;
  let t10;
  if ($[13] !== pool.pool) {
    t10 = truncateAddress(pool.pool);
    $[13] = pool.pool;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== pool.pool || $[16] !== t10 || $[17] !== t8) {
    t11 = <a href={t8} target="_blank" rel="noopener noreferrer" className="truncate text-md font-medium text-text-tertiary underline underline-offset-2" title={t9}>{t10}</a>;
    $[15] = pool.pool;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== pool.network || $[20] !== pool.pool || $[21] !== t11) {
    t12 = <PoolAddressHoverCard poolAddress={t6} network={t7}>{t11}</PoolAddressHoverCard>;
    $[19] = pool.network;
    $[20] = pool.pool;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t5) {
    t13 = <div className="flex min-w-0 items-center gap-2">{t5}{t12}</div>;
    $[23] = t12;
    $[24] = t5;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== hasAccessToPremiumFeatures || $[27] !== onAnalyze || $[28] !== pool.network || $[29] !== pool.pool) {
    t14 = <div className="shrink-0">{hasAccessToPremiumFeatures ? <AnalyzeButton className="uppercase" onClick={() => onAnalyze(pool.pool, pool.network)} /> : <AnalyzeButton className="uppercase" isDisabled={true} />}</div>;
    $[26] = hasAccessToPremiumFeatures;
    $[27] = onAnalyze;
    $[28] = pool.network;
    $[29] = pool.pool;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t13 || $[32] !== t14) {
    t15 = <div className="flex items-start justify-between gap-3">{t13}{t14}</div>;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== pool) {
    t16 = <PoolCardTokenPairLabels pool={pool} />;
    $[34] = pool;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== pool.uniswap) {
    t17 = getProtocolBadgeColor(pool.uniswap);
    $[36] = pool.uniswap;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== pool.uniswap) {
    t18 = formatProtocolBadgeLabel(pool.uniswap);
    $[38] = pool.uniswap;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <span className="font-bold uppercase">{t18}</span>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t17 || $[43] !== t19) {
    t20 = <Badge type="color" size="sm" color={t17}>{t19}</Badge>;
    $[42] = t17;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== pool.feeTier) {
    t21 = <Badge type="color" size="sm" color="gray" className="tabular-nums">{pool.feeTier}</Badge>;
    $[45] = pool.feeTier;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t16 || $[48] !== t20 || $[49] !== t21) {
    t22 = <div className="flex flex-wrap items-center gap-2">{t16}{t20}{t21}</div>;
    $[47] = t16;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== pool.network) {
    t23 = getNetworkBadgeColor(pool.network);
    $[51] = pool.network;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== pool.network || $[54] !== t23) {
    t24 = <div><Badge type="color" size="sm" color={t23}>{pool.network}</Badge></div>;
    $[53] = pool.network;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== has30DayPerformance || $[57] !== has7DayPerformance || $[58] !== hasAccessToPremiumFeatures || $[59] !== hiddenColumnIds || $[60] !== pool) {
    t25 = visibleCardMetricIds(hiddenColumnIds).length > 0 ? <div className="grid grid-cols-2 gap-3">{visibleCardMetricIds(hiddenColumnIds).map(id => <PoolCardMetric key={id} id={id} pool={pool} hasAccessToPremiumFeatures={hasAccessToPremiumFeatures} has7DayPerformance={has7DayPerformance} has30DayPerformance={has30DayPerformance} />)}</div> : null;
    $[56] = has30DayPerformance;
    $[57] = has7DayPerformance;
    $[58] = hasAccessToPremiumFeatures;
    $[59] = hiddenColumnIds;
    $[60] = pool;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t15 || $[63] !== t22 || $[64] !== t24 || $[65] !== t25 || $[66] !== t3 || $[67] !== t4) {
    t26 = <article className={t3} style={t4}>{t15}{t22}{t24}{t25}</article>;
    $[62] = t15;
    $[63] = t22;
    $[64] = t24;
    $[65] = t25;
    $[66] = t3;
    $[67] = t4;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  return t26;
}
function PoolCardMetric(t0) {
  const $ = _c(23);
  const {
    id,
    pool,
    hasAccessToPremiumFeatures,
    has7DayPerformance,
    has30DayPerformance
  } = t0;
  if (id === "apr1d") {
    let t1;
    if ($[0] !== pool.apr1d) {
      t1 = formatApr(pool.apr1d);
      $[0] = pool.apr1d;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <Metric label="APR (1D)" tooltip={APR_1D_TOOLTIP}><span className="truncate text-sm font-medium text-text-primary tabular-nums">{t1}</span></Metric>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (id === "volTvl") {
    let t1;
    if ($[4] !== pool.tvl || $[5] !== pool.volume1d) {
      t1 = formatTurnover(computeTurnover(pool.tvl, pool.volume1d, "1d"));
      $[4] = pool.tvl;
      $[5] = pool.volume1d;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    let t2;
    if ($[7] !== t1) {
      t2 = <Metric label="Vol/TVL (1D)" tooltip={VOL_TVL_1D_TOOLTIP}><span className="truncate text-sm font-medium text-text-primary tabular-nums">{t1}</span></Metric>;
      $[7] = t1;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  if (id === "apr7d") {
    let t1;
    if ($[9] !== has7DayPerformance || $[10] !== hasAccessToPremiumFeatures || $[11] !== pool) {
      t1 = <Metric label="APR (7D)" tooltip={APR_7D_TOOLTIP}>{hasAccessToPremiumFeatures ? has7DayPerformance && pool.apr7d ? <span className="truncate text-sm font-medium text-text-primary tabular-nums">{formatApr(pool.apr7d)}</span> : <div className="h-5 w-16 rounded bg-quaternary" /> : <LockedAprViewLink />}</Metric>;
      $[9] = has7DayPerformance;
      $[10] = hasAccessToPremiumFeatures;
      $[11] = pool;
      $[12] = t1;
    } else {
      t1 = $[12];
    }
    return t1;
  }
  if (id === "volTvl7d") {
    let t1;
    if ($[13] !== hasAccessToPremiumFeatures || $[14] !== pool) {
      t1 = <Metric label="Vol/TVL (7D)" tooltip={VOL_TVL_7D_TOOLTIP}>{hasAccessToPremiumFeatures ? <span className="truncate text-sm font-medium text-text-primary tabular-nums">{formatTurnover(computeTurnover(pool.tvl, pool.volume7d, "7d"))}</span> : <LockedAprViewLink />}</Metric>;
      $[13] = hasAccessToPremiumFeatures;
      $[14] = pool;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    return t1;
  }
  if (id === "apr30d") {
    let t1;
    if ($[16] !== has30DayPerformance || $[17] !== hasAccessToPremiumFeatures || $[18] !== pool) {
      t1 = <Metric label="APR (30D)" tooltip={APR_30D_TOOLTIP}>{hasAccessToPremiumFeatures ? has30DayPerformance && pool.apr30d ? <span className="truncate text-sm font-medium text-text-primary tabular-nums">{formatApr(pool.apr30d)}</span> : <div className="h-5 w-16 rounded bg-quaternary" /> : <LockedAprViewLink />}</Metric>;
      $[16] = has30DayPerformance;
      $[17] = hasAccessToPremiumFeatures;
      $[18] = pool;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    return t1;
  }
  if (id === "volTvl30d") {
    let t1;
    if ($[20] !== hasAccessToPremiumFeatures || $[21] !== pool) {
      t1 = <Metric label="Vol/TVL (30D)" tooltip={VOL_TVL_30D_TOOLTIP}>{hasAccessToPremiumFeatures ? <span className="truncate text-sm font-medium text-text-primary tabular-nums">{formatTurnover(computeTurnover(pool.tvl, pool.volume30d, "30d"))}</span> : <LockedAprViewLink />}</Metric>;
      $[20] = hasAccessToPremiumFeatures;
      $[21] = pool;
      $[22] = t1;
    } else {
      t1 = $[22];
    }
    return t1;
  }
  return null;
}
export function PoolCardSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-start justify-between gap-3"><div className="flex min-w-0 items-center gap-2"><div className="h-8 w-8 shrink-0 animate-pulse rounded bg-quaternary/50" /><div className="h-5 w-24 max-w-full animate-pulse rounded bg-quaternary/50" /></div><div className="h-8 w-24 shrink-0 animate-pulse rounded bg-quaternary/50" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-wrap items-center gap-2"><div className="h-7 w-28 animate-pulse rounded bg-quaternary/50" /><div className="h-6 w-10 animate-pulse rounded bg-quaternary/50" /><div className="h-6 w-14 animate-pulse rounded bg-quaternary/50" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-center gap-2"><div className="size-2 rounded-full animate-pulse rounded bg-quaternary/50" /><div className="h-4 w-18 animate-pulse rounded bg-quaternary/50" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <article className="flex h-full min-w-0 flex-col gap-4 rounded-lg border border-border-primary bg-primary p-4 shadow-xs">{t0}{t1}{t2}<div className="grid grid-cols-2 gap-3">{Array.from({
          length: 4
        }).map(_temp)}</div></article>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp(_, index) {
  return <div key={index} className="flex min-w-0 flex-col gap-2"><div className="h-4 w-16 animate-pulse rounded bg-quaternary/50" /><div className="h-5 w-14 animate-pulse rounded bg-quaternary/50" /></div>;
}